import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Checkbox } from "components/Checkbox";
import { SimpleList } from "components/SimpleList";
import { VerticalSpace } from "components/Spacing";
import {
  useModal,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";

import { apiPost, apiPut, apiDelete, getErrorMessage } from "services/api";
import { useArticles } from "shared-apis";

import styles from "./styles.module.scss";

const ArticleForm = ({ article, onSaved }) => {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [position, setPosition] = useState("99");
  const [isTerminal, setIsTerminal] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
    setName(article?.name ?? "");
    setPosition(String(article?.position ?? 99));
    setIsTerminal(Boolean(article?.showonfront));
  }, [article]);

  const handleSave = async () => {
    if (!name.trim() || position === "") {
      setError("Заполните название и позицию");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        position: Number(position),
        showonfront: isTerminal ? 1 : 0,
      };
      if (article) {
        await apiPut("article/update", {
          ...payload,
          articleid: article.articleid,
        });
      } else {
        await apiPost("article/create", payload);
      }
      queryClient.invalidateQueries(["articles"]);
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={
        <BoldText>{article ? article.name : "Новая статья затрат"}</BoldText>
      }
      footer={
        <ActionsRow>
          {!!error && (
            <RegularText size={13} color="danger" className={styles.footerError}>
              {error}
            </RegularText>
          )}
          <Button
            icon="galochka"
            variant="secondary"
            onClick={handleSave}
            loading={saving}
          >
            Сохранить
          </Button>
        </ActionsRow>
      }
    >
      <div className={styles.form}>
        {!!article && (
          <>
            <Input
              label="Код"
              labelWidth={100}
              value={article.articleid}
              disabled
              inputWidth={100}
            />
            <VerticalSpace size={12} />
          </>
        )}
        <Input
          label="Название"
          labelWidth={100}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          placeholder="Зарплата"
          inputWidth={300}
        />
        <VerticalSpace size={12} />
        <Input
          label="Позиция"
          labelWidth={100}
          type="number"
          value={position}
          onChange={(ev) => setPosition(ev.target.value)}
          inputWidth={100}
        />
        <VerticalSpace size={16} />
        <Checkbox
          label="Доступно в терминале"
          checked={isTerminal}
          onChange={setIsTerminal}
        />
      </div>
    </PageLayout>
  );
};

export const ExpensesPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const articlesQuery = useArticles();
  const articles = useMemo(
    () =>
      [...(articlesQuery.data?.items ?? [])].sort(
        (a, b) => Number(a.position) - Number(b.position)
      ),
    [articlesQuery.data]
  );

  const [activeArticle, setActiveArticle] = useState(null);

  const onDeleteClick = () => {
    if (!activeArticle) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить статью затрат "${activeArticle.name}"?`}
        onConfirm={async () => {
          try {
            await apiDelete("article", {
              articleid: Number(activeArticle.articleid),
            });
            setActiveArticle(null);
            queryClient.invalidateQueries(["articles"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Статьи затрат</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="plus"
              onClick={() => setActiveArticle(null)}
              title="Добавить статью затрат"
            />
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!activeArticle}
              title="Удалить"
            />
          </ActionsRow>
        }
      >
        <SimpleList
          items={articles}
          rowKey="articleid"
          activeId={activeArticle?.articleid}
          loading={articlesQuery.isLoading}
          error={
            articlesQuery.isError ? getErrorMessage(articlesQuery.error) : ""
          }
          onRetry={articlesQuery.refetch}
          onRowClick={setActiveArticle}
        />
      </PageLayout>
      <ArticleForm
        article={activeArticle}
        onSaved={() => setActiveArticle(null)}
      />
    </div>
  );
};
